import { lazy, memo, useState } from 'react';
import { ContentItemType, type Message } from '../../gql/graphql';
import { formatTimestamp } from '@utils/datetime';
import { twMerge } from 'tailwind-merge';
import { KawaiiButton } from '@components/button';
import { Check, Copy, RefreshCw, Reply } from 'lucide-react';
import { useTheme } from '@components/theme';
import theme from './theme';
import whiteTheme from './whiteTheme';

const SyntaxHighlighter = lazy(() => import('./syntax'));
const ReactMarkdown = lazy(() => import('react-markdown'));

interface CodeBlockWithCopy {
  className?: string;
  children?: React.ReactNode;
}

const CodeBlockWithCopy: React.FC<CodeBlockWithCopy> = ({ className, children, ...props }) => {
  const [buttonText, setButtonText] = useState('Copy');
  const match = /language-(\w+)/.exec(className || '');
  const codeString = String(children ?? '').replace(/\n$/, '');
  const { isDark } = useTheme();

  const handleCopy = async () => {
    if (!codeString) return;
    try {
      await navigator.clipboard.writeText(codeString);
      setButtonText('Copied!');
      setTimeout(() => setButtonText('Copy'), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
      setButtonText('Error');
      setTimeout(() => setButtonText('Copy'), 2000);
    }
  };

  if (typeof children !== 'string' || !children.trim()) {
    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  }

  return match ? (
    <div className="group relative rounded-lg bg-blue-100/50 dark:bg-gray-700/60 flex flex-col w-full pt-1 my-1 gap-1 pb-4">
      <div className="sticky top-1 h-4 self-end z-50 transition-opacity duration-200">
        <KawaiiButton
          onClick={handleCopy}
          className="p-2 px-4 mx-2 rounded-md text-sm"
          variant="secondary"
        >
          {buttonText === 'Copy' ? <Copy size={16} /> : <Check size={16} />}{' '}
        </KawaiiButton>
      </div>
      <SyntaxHighlighter
        style={isDark ? theme : whiteTheme}
        language={match[1]}
        PreTag="div"
        {...props}
      >
        {codeString}
      </SyntaxHighlighter>
    </div>
  ) : (
    <code className={className} {...props}>
      {children}
    </code>
  );
};

const renderContent = (content: Message['content']) => {
  if (typeof content === 'string') {
    // If it's just a string, render it with Markdown too!
    return (
      <ReactMarkdown
        components={{
          // This part handles the code blocks, ze~!
          code: CodeBlockWithCopy,
        }}
      >
        {content}
      </ReactMarkdown>
    );
  }

  // If it's an array, map through it like before
  return content.map((item, index) => (
    <MessageContent key={`${item.type}-${index}`} item={item} uniqueId={`${item.type}-${index}`} />
  ));
};

const MessageContent = ({
  item,
}: {
  item: Message['content'][0];
  // Removed uniqueId as it wasn't used in the provided snippet
}) => {
  const markdownComponents: React.ComponentProps<typeof ReactMarkdown>['components'] = {
    // Apply margin to paragraphs
    p: (props) => <p className="mb-4 last:mb-0" {...props} />,
    // Style unordered lists
    ul: (props) => <ul className="list-disc list-inside mb-4 ml-4 last:mb-0" {...props} />,
    // Style ordered lists
    ol: (props) => <ol className="list-decimal list-inside mb-4 ml-4 last:mb-0" {...props} />,
    // Style list items
    li: (props) => <li className="mb-1 last:mb-0" {...props} />,
    // Apply margin to code blocks (rendered by CodeBlockWithCopy)
    code: (props) => <CodeBlockWithCopy {...props} />,
    // Apply margin to pre blocks (used by default for code blocks if `code` isn't specified)
    pre: (props) => <pre className="mb-4 last:mb-0" {...props} />,
  };

  switch (item.type) {
    case ContentItemType.Text:
      return <ReactMarkdown components={markdownComponents}>{item.text ?? ''}</ReactMarkdown>;
    case ContentItemType.ToolUse:
      return (
        <div className="bg-pink-100 dark:bg-pink-900 p-3 rounded-lg my-2 mb-4">
          {' '}
          {/* Added mb-4 */}
          <div className="text-xs text-pink-600 dark:text-pink-300 font-semibold mb-1">
            Using tool: {item.name}
          </div>
          {/* Consider a dedicated JSON viewer/highlighter component later */}
          <pre className="text-xs overflow-x-auto bg-white dark:bg-gray-800 p-2 rounded scrollbar-thin scrollbar-thumb-pink-300 dark:scrollbar-thumb-pink-700">
            {JSON.stringify(item.input, null, 2)}
          </pre>
        </div>
      );
    case ContentItemType.ToolResult:
      // Adjusted max-width, padding, margin
      return (
        <div className="w-full bg-green-100 dark:bg-green-900 p-3 rounded-lg my-2 mb-4">
          <div className="text-xs text-green-600 dark:text-green-300 font-semibold mb-1">
            Tool result:
          </div>
          {/* Apply same markdown components here */}
          <ReactMarkdown components={markdownComponents}>{item.content ?? ''}</ReactMarkdown>
        </div>
      );
    default:
      // It's good practice to return null or a placeholder
      return null;
  }
};

const ChatMessageComponent: React.FC<{ message: Message }> = ({ message }) => (
  <div
    className={`flex mx-1 nth-last-3:mb-5 my-2 flex-col ${message.role === 'user' ? 'items-end' : 'items-start'}`}
  >
    <div
      className={twMerge(
        `max-w-[var(--max-width-magic)] flex group flex-col gap-2 px-4 py-2 rounded-2xl border 
        shadow-md hover:shadow-lg transition-shadow duration-200 ease-in-out`,
        message.role === 'user'
          ? `  bg-pink-200 dark:bg-pink-900/80 rounded-tr-sm 
             border-pink-100 dark:border-pink-700`
          : `bg-purple-100/70 dark:bg-gray-800/80 rounded-tl-sm
             border-purple-50 dark:border-gray-700`,
      )}
    >
      <div className="flex w-full gap-4 items-center justify-between">
        <div className="text-sm text-gray-500 dark:text-gray-300 font-medium">
          {message.role === 'user' ? 'Onii-chan' : 'Mochi-chan'}
        </div>

        <div className="sticky top-1 flex gap-4 items-center self-end z-50 transition-opacity duration-300">
          <div className="text-xs text-gray-500 dark:text-gray-300">
            {formatTimestamp(message.timestamp)}
          </div>
        </div>
      </div>
      <div className="break-words text-gray-600 dark:text-gray-100 prose dark:prose-invert prose-sm max-w-none">
        {renderContent(message?.content)}
      </div>
      <div className="group flex gap-1">
        <KawaiiButton
          onClick={() => {
            if (message.content[0]?.text) {
              navigator.clipboard.writeText(message.content[0]?.text);
            }
          }}
          className="px-3 py-1 border-none rounded-md  bg-transparent dark:bg-transparent text-sm"
          variant="secondary"
        >
          <Copy size={20} />
        </KawaiiButton>
        <KawaiiButton
          onClick={() => {
            console.log('refresh');
          }}
          className="px-3 py-1 border-none rounded-md  bg-transparent dark:bg-transparent text-sm"
          variant="secondary"
        >
          <RefreshCw size={20} />
        </KawaiiButton>
        <KawaiiButton
          onClick={() => {
            console.log('reply');
          }}
          className="px-3 py-1 border-none rounded-md  bg-transparent dark:bg-transparent text-sm"
          variant="secondary"
        >
          <Reply size={20} />
        </KawaiiButton>
      </div>
    </div>
  </div>
);

export const ChatMessage = memo(ChatMessageComponent);
